/**
 * 正式URL(canonical origin)と「旧URLで遊んでいるか」の判定。
 *
 * 2026-09-02 に本番を `https://paltyping.pages.dev` から `https://paltown.app` へ移した
 * (docs/03_architecture.md §8.1)。旧URLは移行期間中だけ併存させるので、
 * そこへ来てしまった匿名ユーザーに「あたらしい ばしょ」へ移ってもらう案内を出す。
 *
 * 判定は純粋関数にしてある(引数でオリジンを受ける = 単体テストできる)。
 * `window.location` を読むのは呼び出し側の `currentOrigin()` だけ。
 */

/** 正式URLの既定値。`VITE_CANONICAL_ORIGIN` で上書きできる(ステージングは dev.paltown.app)。 */
export const DEFAULT_CANONICAL_ORIGIN = 'https://paltown.app';

/** Cloudflare Pages の既定ドメイン。ここに来ていたら「旧URL」とみなす。 */
const PAGES_DEV_SUFFIX = '.pages.dev';

/** `https://example.com/foo` → `https://example.com`。壊れた値なら null。 */
function parseOrigin(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/**
 * いま見ているオリジンが「旧URL」か。
 *
 * true になるのは **正式URLと違い、かつホスト名が `*.pages.dev` のとき** だけ。
 * localhost(開発・E2E)・正式URL・`dev.paltown.app` では false になる。
 */
export function isLegacyHost(currentOrigin: string, canonicalOrigin: string): boolean {
  const current = parseOrigin(currentOrigin);
  const canonical = parseOrigin(canonicalOrigin);
  if (current === null || canonical === null) return false;
  if (current.origin === canonical.origin) return false;
  return current.hostname.endsWith(PAGES_DEV_SUFFIX);
}

const RAW_CANONICAL: unknown = import.meta.env.VITE_CANONICAL_ORIGIN;

/** 正式URL(env で上書き可)。末尾のスラッシュは付けない形に正規化する。 */
export function canonicalOrigin(
  raw: string | undefined = typeof RAW_CANONICAL === 'string' ? RAW_CANONICAL : undefined,
): string {
  const trimmed = raw?.trim();
  if (trimmed === undefined || trimmed === '') return DEFAULT_CANONICAL_ORIGIN;
  return parseOrigin(trimmed)?.origin ?? DEFAULT_CANONICAL_ORIGIN;
}

/** いま見ているオリジン(SSR や非ブラウザ環境では空文字)。 */
export function currentOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}

/** 画面から使う入口。旧URLで遊んでいるときだけ true。 */
export function shouldShowMoveNotice(): boolean {
  return isLegacyHost(currentOrigin(), canonicalOrigin());
}
